import { ref, computed } from 'vue'
import { useTranslate } from "@shared/composable/useTranslate";
import { getDocumentLinks } from '@shared/utils/getDocumentLinks';
import { DOCS } from '@shared/types';

export function useApplyLinks(props: Record<string, any>) {
  const { translate } = useTranslate();
  const countryCode = props.pageData?.countryCode;
  const locale = props.locale;

  const isWebDeeplinkActive = !!props.pageData?.deeplinkSettings?.deeplinkWebStatus;
  const isRussia = countryCode === 'RU';
  const russianKey = 'generate.legalDocInfoElement.expandedRussiaAdditional';
  const RUSSIA_DOCUMENTS = {
    payment_aggregator: 'https://3p-assets.cdnztl.com/docs/2025/09/10/jigle-payment-terms-ru.pdf',
  }

  const docLinks = getDocumentLinks({
    countryCode: countryCode?.toLowerCase() ?? 'en',
    lang: locale,
    links: props.pageData?.docLinks || {}
  });

  const LINKS_BY_ATTR = {
    "data-privacy-policy-link": props.pageData?.applicationLinks?.privacyUrl,
    "data-terms-of-use-link": props.pageData?.applicationLinks?.termsOfServiceUrl,
    "data-customer-service-link": props.pageData?.applicationLinks?.customerSupportUrl,
    "data-generic-app-link": props.pageData?.applicationLinks?.[isWebDeeplinkActive ? 'deeplinkWeb' : 'genericDownloadUrl'],
    "data-zotlo-privacy-policy-link": docLinks?.[DOCS.PRIVACY_POLICY],
    "data-zotlo-terms-of-use-link": docLinks?.[DOCS.TERMS_OF_SERVICE],
    "data-payment-aggregator": RUSSIA_DOCUMENTS.payment_aggregator,
  }

  const params = {
    additional: isRussia ? `<p>${translate(russianKey, props.locale)}</p>` : '',
    ...props.element.options?.params
  }

  const translated = translate(props.value, props.locale, params);

  const textValue = ref(
    props.isEditorMode
      ? translated
      : Object.entries(LINKS_BY_ATTR).reduce((acc, [key, value]) => {
          return acc.replace(new RegExp(key, 'g'), `${key} href="${value || '#'}" target="_blank"`)
        }, translated)
  );

  const linksAttrs = computed(() => {
    if (props.isEditorMode || props.element.tagName !== 'a') return {};

    return Object.entries(props.element.attr)
      .filter(([key]) => /^data-/.test(key))
      .reduce((acc, item) => {
        const key = item[0] as keyof typeof LINKS_BY_ATTR;
        const val = LINKS_BY_ATTR[key];
        if (val) {
          acc['href'] = val || '#';
          acc['target'] = '_blank'
        }
        return acc;
      }, {} as Record<string, any>)
  })

  return {
    textValue,
    linksAttrs,
  }
}
